import { useQuery } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApiError, auth, type SessionInfo } from './lib/api.js';
import { LoginPage } from './pages/Login.js';
import { OperatorCustomersPage } from './pages/OperatorCustomers.js';
import { OverviewPage } from './pages/Overview.js';
import { Shell } from './pages/Shell.js';
import { ContentPage, ContentDetailPage } from './pages/Content.js';
import { GbpPage } from './pages/Gbp.js';
import { AieoPage } from './pages/Aieo.js';
import { AuditsPage } from './pages/Audits.js';
import { SiteHealthPage } from './pages/SiteHealth.js';
import { BackupsPage } from './pages/Backups.js';
import { RankTrackerPage } from './pages/RankTracker.js';
import { SettingsPage } from './pages/Settings.js';
import { BillingPage } from './pages/Billing.js';
import { OperatorCustomerDetailPage } from './pages/OperatorCustomerDetail.js';
import { OperatorPortfolioPage } from './pages/OperatorPortfolio.js';
import { OperatorFeedPage } from './pages/OperatorFeed.js';
import { OperatorAlertsPage } from './pages/OperatorAlerts.js';
import { OperatorAutoreasonRunsPage } from './pages/OperatorAutoreasonRuns.js';
import { IntegrationsPage } from './pages/Integrations.js';
import { ReviewsPage } from './pages/Reviews.js';

export function App() {
  const session = useQuery<SessionInfo, ApiError>({
    queryKey: ['auth', 'me'],
    queryFn: auth.me,
    retry: false,
    staleTime: 60_000,
  });

  if (session.isLoading) {
    return <FullScreenLoader />;
  }

  // Auth-failed (401) → show login. Other errors → show login w/ error msg.
  if (session.isError || !session.data) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<RedirectToLogin />} />
      </Routes>
    );
  }

  const data = session.data;
  const isOperator = data.user.role === 'operator';
  return (
    <Shell session={data}>
      <Routes>
        {/* Operator role lands on customers list by default; / redirects there.
            Owner/member role lands on Overview at /. */}
        <Route
          path="/"
          element={isOperator ? <Navigate to="/operator/customers" replace /> : <OverviewPage session={data} />}
        />
        {isOperator && (
          <Route path="/operator/customers" element={<OperatorCustomersPage />} />
        )}
        {isOperator && (
          <Route path="/operator/customers/:accountId" element={<OperatorCustomerDetailPage />} />
        )}
        {isOperator && (
          <Route path="/operator/portfolio" element={<OperatorPortfolioPage />} />
        )}
        {isOperator && (
          <Route path="/operator/feed" element={<OperatorFeedPage />} />
        )}
        {isOperator && (
          <Route path="/operator/alerts" element={<OperatorAlertsPage />} />
        )}
        {isOperator && (
          <Route path="/operator/autoreason" element={<OperatorAutoreasonRunsPage />} />
        )}
        {isOperator && (
          <Route path="/operator" element={<Navigate to="/operator/customers" replace />} />
        )}
        {!isOperator && <Route path="/content" element={<ContentPage session={data} />} />}
        {!isOperator && <Route path="/content/:id" element={<ContentDetailPage session={data} />} />}
        {!isOperator && <Route path="/gbp" element={<GbpPage session={data} />} />}
        {!isOperator && <Route path="/reviews" element={<ReviewsPage />} />}
        {!isOperator && <Route path="/aieo" element={<AieoPage />} />}
        {!isOperator && <Route path="/audits" element={<AuditsPage />} />}
        {!isOperator && <Route path="/site-health" element={<SiteHealthPage session={data} />} />}
        {!isOperator && <Route path="/backups" element={<BackupsPage />} />}
        {!isOperator && <Route path="/rank-tracker" element={<RankTrackerPage session={data} />} />}
        {!isOperator && <Route path="/integrations" element={<IntegrationsPage />} />}
        {!isOperator && <Route path="/settings" element={<SettingsPage />} />}
        {!isOperator && <Route path="/billing" element={<BillingPage />} />}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

function RedirectToLogin() {
  const loc = useLocation();
  const next = encodeURIComponent(loc.pathname + loc.search);
  return <Navigate to={`/login?next=${next}`} replace />;
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center text-zinc-500">
      <div className="animate-pulse">Loading…</div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="p-12 text-zinc-500">
      <h1 className="text-2xl font-semibold text-zinc-100">Not found</h1>
      <p className="mt-2 text-sm">The page you're looking for doesn't exist yet.</p>
    </div>
  );
}
